<script>
let preparedNavigation = null;
</script>

<script setup>
import MenuEntry from './menu-entry.vue';
import { menu } from '@src/menu';
import { computed, ref, watch } from 'vue';
import { prepareNavigation, useNavigation } from './navigation';
import { useRouterComposable } from '@router/router-composable';
import { useCurrentUserComposable } from '@src/user/composable/current-user-composable';
import Text from '@components/text.vue';
import mobileDetectStore from '@src/application/store/mobile-detect-store';
import showSideBarStore from '@src/application/store/show-side-bar-store';
import MenuSearch from '@components/menu/menu-search.vue';
import MenuHeader from '@components/menu/menu-header.vue';
import { closeLoader, openLoader } from '@utils/loader-util';
import apiClient from '@src/api/api-client';
import FavoriteEntries from '../../favorite/components/favorite-entries.vue';

const version = import.meta.env.BB_APP_CURRENT_VERSION ? import.meta.env.BB_APP_CURRENT_VERSION : 'dev-version';

mobileDetectStore.isMobile.watch(() => {
    showSideBarStore.showSideBar.data = !mobileDetectStore.isMobile.data;
}, { immediate: true });

const loadSideBar = ref(false);
showSideBarStore.showSideBar.watch(() => {
    if (loadSideBar.value || !showSideBarStore.showSideBar.data) {
        return;
    }
    loadSideBar.value = true;
}, { immediate: true });

const searchString = ref(null);
const { currentRoute, resolveRoute } = useRouterComposable();
const { currentUser } = useCurrentUserComposable();
const { visible, toggle, searchTerm } = useNavigation(
    preparedNavigation ??= prepareNavigation(menu, (name) => resolveRoute({ name })),
    computed(() => currentUser.value?.roles ?? []),
    computed(() => currentRoute.value.name),
    searchString,
);
watch(searchString, async () => {
    const s = searchString.value;
    if (!s) {
        return;
    }

    const pattern = {
        'AGU\\d+': '/accounting/credit-note/',
        'BIK\\d+': '/accounting/invoice/',
        'CLA\\d+': '/claim/edit/',
        'EGU\\d+': '/accounting/incoming-credit-note/',
        'LIS\\d+': '/accounting/delivery-note/',
        'RE\\d+': '/revocation/edit/',
        'ZE\\d+': '/incoming-payment/',
        'ZA\\d+': '/outgoing-payment/edit/',
        'WO\\d+': '/workshop/order/edit/',
    };

    for (const [p, url] of Object.entries(pattern)) {
        const r = new RegExp(p);
        if (s.match(r) === null) {
            continue;
        }
        openLoader();
        // eslint-disable-next-line no-await-in-loop
        const id = (await apiClient.get('/be/search/id-by-doc-num/' + encodeURIComponent(s))).data;
        closeLoader();
        if (id) {
            window.location.href = url + id;
        }
        return;
    }

    if (s.match(/D0\d+/) !== null) {
        window.location.href = '/customer/edit/' + encodeURIComponent(s);
    }
});
</script>

<template>
    <!-- eslint-disable-next-line vue/no-multiple-template-root -->
    <template v-if="loadSideBar">
        <div
            v-show="showSideBarStore.showSideBar.data"
            :class="[$style.sidebar, mobileDetectStore.isMobile.data ? $style.mobile : $style.desktop]"
        >
            <MenuHeader/>
            <MenuSearch @search="searchString = $event"/>
            <FavoriteEntries/>
            <div :class="$style.sidebar__content">
                <MenuEntry
                    v-for="row in visible"
                    :key="row.entry.id"
                    v-bind="row"
                    :active-search="Boolean(searchTerm)"
                    @toggle="toggle"
                />
                <Text v-if="searchTerm && visible.length === 0" :class="$style.no_entry">Keine Einträge</Text>
                <p :class="$style.version">{{ version }}</p>
            </div>
        </div>
    </template>
</template>

<style lang="scss" module>
@use '@backgrounds' as *;

.sidebar {
    min-height: 100vh;
    background: $sidebar-background;

    .sidebar__content {
        margin-bottom: .5em;
    }
    .no_entry {
        padding: .85em 1.25em;
        line-height: 1.3;
        color: #fff;
    }
    .version {
        font-size: 75%;
        color: #fff;
        text-align: center;
    }

    &.mobile {
        position: fixed;
        z-index: 97;
        width: 100vw;
        height: 100vh;
        overflow: auto;

        .version {
            margin-top: 1.3em;
        }
    }

    &.desktop {
        position: relative;
        flex-shrink: 0;
        width: 15em;

        .sidebar__content {
            margin-bottom: 2em;
        }
        .version {
            position: absolute;
            bottom: 10px;
            width: 100%;
        }
    }
}
</style>
